import { deiterateAPI } from '../js/external-js/variableModules/utils/deiterateAPI.util.js';
import { downloadFromSDK } from '../js/external-js/variableModules/downloadFromSDK.module.js';
import { firebaseConfig, storageRef } from '../js/external-js/config/main_config';

// Types
export interface Template {
  id: string;
  name: string;
  description?: string;
  categoryId: string;
  previewUrl: string;
  downloadUrl?: string;
  filename?: string;
  fileExtension?: string;
  metadata: {
    author: string;
    version: string;
    lastUpdated?: string;
    tags?: string[];
  };
  templateData?: any;
}

export interface Category {
  id: string;
  name: string;
  groupName: string;
  icon: string;
  items?: Category[];
}

export interface TemplatesData {
  categories: Category[];
  templates: Template[];
}

/**
 * Get templates data from SSR flags or API
 */
export async function getTemplatesData(organizationId: string, userToken?: string): Promise<TemplatesData> {
  try {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    // First try to get from SSR flags (legacy support)
    const ssrData = (window as any)._SSR_FLAGS?.additives?.templatesLibrary;
    if (ssrData) {
      console.log('[TemplateService] Using SSR data:', ssrData);
      
      // Generate secure URLs for templates
      const templatesWithUrls = await Promise.all(
        ssrData.templates.map(async (template: any) => {
          const { downloadKey } = await generateSecureKeys(template.id, organizationId);
          
          // Process template name to replace 'de.iterate' pattern with company name
          const companyName = window.companyName || 'Your Organization';
          const processedName = template.name.replace(/^de\.iterate/i, companyName);
          
          const filename = `${processedName} ${template.metadata.version}${template.fileExtension || '.docx'}`;
          const encodedFilename = encodeURIComponent(filename);
          const uid = (window as any)._SSR_FLAGS?.UID;
          
          return {
            ...template,
            name: processedName,
            downloadUrl: `/api/templates/files/document/${encodedFilename}?key=${downloadKey}&uid=${uid}`,
            previewUrl: `/api/templates/files/preview/${template.id}.png?key=${downloadKey}&uid=${uid}`,
            filename: filename,
            originalId: template.id
          };
        })
      );

      return {
        categories: ssrData.categories,
        templates: templatesWithUrls
      };
    }

    // Fallback to API call
    console.log('[TemplateService] Fetching from API for organizationId:', organizationId);
    const response = await deiterateAPI.GET(`/templates/library?companyID=${organizationId}`, {
      companyID: organizationId,
      ...(userToken && { token: userToken })
    });

    if (!response || !response.success || !response.data) {
      throw new Error(response?.error || 'No templates data received from API');
    }

    // Process template names to replace 'de.iterate' pattern with company name
    const companyName = window.companyName || 'Your Organization';
    const processedTemplates = response.data.templates.map((template: any) => {
      const processedName = template.name.replace(/^de\.iterate/i, companyName);
      return {
        ...template,
        name: processedName
      };
    });

    return {
      ...response.data,
      templates: processedTemplates
    };
  } catch (error) {
    console.error('[TemplateService] Error fetching templates:', error);
    throw new Error('Failed to load templates data');
  }
}

/**
 * Generate secure keys for template file access
 */
export async function generateSecureKeys(templateId: string, organizationId: string): Promise<{ downloadKey: string }> {
  try {
    console.log('[TemplateService] Generating secure key for template:', templateId);
    
    const response = await fetch(`/api/templates/keys/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        templateId,
        organizationId,
        fileType: 'document'
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to generate secure key: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[TemplateService] Generated key:', data);

    return {
      downloadKey: data.accessKey
    };
  } catch (error) {
    console.error('[TemplateService] Error generating secure keys:', error);
    throw new Error('Failed to generate secure access keys');
  }
}

/**
 * Download a template file
 */
export async function downloadTemplate(template: Template, organizationId: string): Promise<void> {
  try {
    // Use Firebase Storage SDK helper to download from storage path
    const fileName = decodeURIComponent(
      template.downloadUrl || `${template.name}${template.fileExtension || '.docx'}`
    );
    const storagePath = `templates/templateLibrary/documents/${fileName}`;

    await downloadFromSDK(
      firebaseConfig,
      storageRef,
      'download',
      null,
      fileName,
      storagePath,
      false,
      {}
    );

    console.log('[TemplateService] Template downloaded via SDK:', template.name);
  } catch (error) {
    console.error('[TemplateService] Error downloading template:', error);
    throw new Error('Failed to download template');
  }
}

/**
 * Get template preview URL
 */
export function getTemplatePreviewUrl(template: Template): string {
  if (!template.downloadUrl) {
    return '';
  }

  // Add preview parameters to the download URL
  const url = new URL(template.downloadUrl, window.location.origin);
  url.searchParams.set('preview', 'true');
  url.searchParams.set('disposition', 'inline');
  
  return url.toString();
}

/**
 * Search templates by query
 */
export function searchTemplates(templates: Template[], query: string): Template[] {
  if (!query.trim()) {
    return templates;
  }

  const searchTerm = query.toLowerCase();
  
  return templates.filter(template => 
    template.name.toLowerCase().includes(searchTerm) ||
    template.description?.toLowerCase().includes(searchTerm) ||
    template.metadata.author.toLowerCase().includes(searchTerm) ||
    template.metadata.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
  );
}

/**
 * Filter templates by category
 */
export function filterTemplatesByCategory(
  templates: Template[], 
  categories: Category[], 
  categoryId: string
): Template[] {
  if (categoryId === 'all') {
    return templates;
  }

  // Find the category and get all related category IDs (including subcategories)
  const category = categories.find(c => c.id === categoryId);
  const categoryIds = [categoryId];
  
  if (category?.items) {
    category.items.forEach(item => categoryIds.push(item.id));
  }

  return templates.filter(template => categoryIds.includes(template.categoryId));
}

/**
 * Get template by ID
 */
export function getTemplateById(templates: Template[], templateId: string): Template | null {
  return templates.find(template => template.id === templateId) || null;
}

/**
 * Suggest a new template
 */
export async function suggestTemplate(suggestion: {
  name: string;
  description: string;
  category: string;
  organizationId: string;
  userEmail?: string;
  userName?: string;
}): Promise<void> {
  try {
    console.log('[TemplateService] Submitting template suggestion:', {
      name: suggestion.name,
      category: suggestion.category,
      organizationId: suggestion.organizationId
    });
    
    const response = await deiterateAPI.POST('/templates/suggestions', {
      body: JSON.stringify({
        name: suggestion.name,
        description: suggestion.description,
        category: suggestion.category,
        organizationId: suggestion.organizationId,
        userEmail: suggestion.userEmail,
        userName: suggestion.userName
      }),
      companyID: suggestion.organizationId
    });

    if (!response || !response.success) {
      throw new Error(response?.error || 'Unknown error occurred');
    }

    console.log('[TemplateService] Template suggestion submitted successfully:', response.suggestionId);
  } catch (error) {
    console.error('[TemplateService] Error submitting template suggestion:', error);
    throw new Error('Failed to submit template suggestion. Please try again later.');
  }
}

/**
 * Get template statistics
 */
export function getTemplateStats(templates: Template[]): {
  total: number;
  byCategory: Record<string, number>;
  byAuthor: Record<string, number>;
} {
  const stats = {
    total: templates.length,
    byCategory: {} as Record<string, number>,
    byAuthor: {} as Record<string, number>
  };

  templates.forEach(template => {
    // Count by category
    stats.byCategory[template.categoryId] = (stats.byCategory[template.categoryId] || 0) + 1;
    
    // Count by author
    stats.byAuthor[template.metadata.author] = (stats.byAuthor[template.metadata.author] || 0) + 1;
  });

  return stats;
}
